import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  Fade,
} from '@mui/material';
import { Add } from '@mui/icons-material';

const EmptyState = ({
  icon: Icon,
  title,
  description,
  features = [],
  buttonText = "Crear Nuevo",
  onButtonClick,
  helpText,
  maxWidth = "md",
  iconSize = 80,
  iconColor = "#55b4ac",
}) => {
  return (
    <Fade in={true} timeout={800}>
      <Container maxWidth={maxWidth} sx={{ py: 8 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            py: 6,
            px: 4,
            background: 'linear-gradient(135deg, rgba(85, 180, 172, 0.03) 0%, rgba(24, 88, 107, 0.03) 100%)',
            borderRadius: 4,
            border: '1px solid rgba(85, 180, 172, 0.1)',
          }}
        >
          {/* Animated Icon */}
          <Box
            sx={{
              position: 'relative',
              mb: 4,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(85, 180, 172, 0.1), rgba(24, 88, 107, 0.1))',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    transform: 'translate(-50%, -50%) scale(1)',
                    opacity: 0.7,
                  },
                  '50%': {
                    transform: 'translate(-50%, -50%) scale(1.1)',
                    opacity: 0.3,
                  },
                },
              },
            }}
          >
            <Icon
              sx={{
                fontSize: iconSize,
                color: iconColor,
                position: 'relative',
                zIndex: 1,
                animation: 'float 3s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0px)' },
                  '50%': { transform: 'translateY(-10px)' },
                },
              }}
            />
          </Box>

          {/* Title */}
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{
              mb: 2,
              background: 'linear-gradient(45deg, #55b4ac, #18586b)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {title}
          </Typography>

          {/* Description */}
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 500, lineHeight: 1.6 }}
          >
            {description}
          </Typography>

          {/* Feature Cards */}
          {features.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 4, maxWidth: 600 }}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.7)',
                      border: '1px solid rgba(85, 180, 172, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(85, 180, 172, 0.2)',
                      },
                    }}
                  >
                    {feature.icon && (
                      <feature.icon sx={{ color: iconColor, mb: 1 }} />
                    )}
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      {feature.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {/* CTA Button */}
          {onButtonClick && (
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={onButtonClick}
              color="primarybutton"
              sx={{
                background: 'linear-gradient(45deg, #55b4ac, #18586b)',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
//                borderRadius: 3,
                textTransform: 'none',
                boxShadow: '0 4px 20px rgba(85, 180, 172, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(45deg, #18586b, #55b4ac)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 30px rgba(85, 180, 172, 0.4)',
                },
              }}
            >
              {buttonText}
            </Button>
          )}

          {/* Help Text */}
          {helpText && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 3, opacity: 0.8 }}
            >
              {helpText}
            </Typography>
          )}
        </Box>
      </Container>
    </Fade>
  );
};

export default EmptyState;
